/**
 * Persistent, anonymous per-device visitor id used to dedupe "unique visitors"
 * across sessions in admin analytics. Stored in localStorage, seeded with a
 * lightweight device fingerprint hash so it is stable per browser+device.
 *
 * Contains no personal data — it is a random UUID plus a coarse device hash.
 */
import { getDeviceFingerprint } from "@/lib/deviceFingerprint";

const KEY = "lx_visitor_id";
let inMemoryVisitorId: string | null = null;

function deviceHash(): string {
  try {
    const fp = getDeviceFingerprint();
    const seed = [
      fp.device_brand,
      fp.device_model,
      fp.device_type,
      fp.os,
      fp.browser,
      typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : "",
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    ].join("|");
    let h = 0;
    for (let i = 0; i < seed.length; i++) {
      h = (h << 5) - h + seed.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h).toString(36);
  } catch {
    return "unknown";
  }
}

/** Stable anonymous device id. Never throws (private-mode safe). */
export function getVisitorId(): string {
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = `${deviceHash()}-${crypto.randomUUID()}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch {
    if (!inMemoryVisitorId) inMemoryVisitorId = `${deviceHash()}-${crypto.randomUUID()}`;
    return inMemoryVisitorId;
  }
}
